import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import ThermostatRoundedIcon from '@mui/icons-material/ThermostatRounded';
import demoRequestLeadServices from 'app/services/demo-request-lead-services';

const TOKEN_KEY = 'demo_lead_token';
const MAX_TOTAL_DEVICES = 20;
const MAX_DEVICES_PER_SLIDER = 10;

const DEFAULT_FORM = {
  first_name: '',
  last_name: '',
  company_name: '',
  company_sector: '',
  email: '',
  phone: '',
  location_name: '',
  measure_temperature: true,
  measure_humidity: true,
  fridges_qty: 1,
  freezers_qty: 0,
  ovens_qty: 0,
  measure_energy: false,
  pm_energy_devices_qty: 0,
  energy_devices_qty: 0,
  measure_people_count: false,
  people_count_devices_qty: 0,
  source: 'xpit_connect_demo_form',
};

const STEPS = ['Inicio', 'Identidad', 'Escenario', 'Sensores', 'Acceso'];
const numberFields = ['fridges_qty', 'freezers_qty', 'ovens_qty', 'pm_energy_devices_qty', 'energy_devices_qty', 'people_count_devices_qty'];
const clampDeviceCount = (value) => Math.max(0, Math.min(MAX_DEVICES_PER_SLIDER, Number(value || 0)));

const climateDevicesCount = (form) => (
  form.measure_temperature || form.measure_humidity
    ? Number(form.fridges_qty || 0) + Number(form.freezers_qty || 0) + Number(form.ovens_qty || 0)
    : 0
);

const energyDevicesCount = (form) => (
  form.measure_energy
    ? Number(form.pm_energy_devices_qty || 0) + Number(form.energy_devices_qty || 0)
    : 0
);

const peopleDevicesCount = (form) => (
  form.measure_people_count ? Number(form.people_count_devices_qty || 0) : 0
);

const calculateSimulationCount = (form) => (
  climateDevicesCount(form) + energyDevicesCount(form) + peopleDevicesCount(form)
);

const isDeviceFieldEnabled = (form, field) => {
  if (['fridges_qty', 'freezers_qty', 'ovens_qty'].includes(field)) return form.measure_temperature || form.measure_humidity;
  if (['pm_energy_devices_qty', 'energy_devices_qty'].includes(field)) return form.measure_energy;
  if (field === 'people_count_devices_qty') return form.measure_people_count;
  return false;
};

const SOLUTIONS = [
  {
    key: 'climate',
    icon: ThermostatRoundedIcon,
    title: 'Cadena de frío',
    subtitle: 'Temperatura y humedad',
    color: '#2EB5C9',
  },
  {
    key: 'energy',
    icon: BoltRoundedIcon,
    title: 'Energía',
    subtitle: 'Consumo y encendido',
    color: '#F39711',
  },
  {
    key: 'people',
    icon: GroupsRoundedIcon,
    title: 'Personas',
    subtitle: 'Aforo y flujo',
    color: '#5C149B',
  },
];

const normalizeLead = (payload = {}) => {
  const lead = payload.demo_request_lead || payload;
  return Object.keys(DEFAULT_FORM).reduce((acc, key) => {
    if (numberFields.includes(key)) {
      acc[key] = clampDeviceCount(lead[key] ?? DEFAULT_FORM[key]);
      return acc;
    }
    acc[key] = lead[key] ?? DEFAULT_FORM[key];
    return acc;
  }, {});
};

const solutionEnabled = (form, key) => {
  if (key === 'climate') return form.measure_temperature || form.measure_humidity;
  if (key === 'energy') return form.measure_energy;
  if (key === 'people') return form.measure_people_count;
  return false;
};

const DemoChip = ({ active, label }) => (
  <Chip
    size="small"
    label={label}
    sx={{
      bgcolor: active ? alpha('#5C149B', 0.1) : alpha('#8595A6', 0.12),
      color: active ? '#5C149B' : '#8595A6',
      fontWeight: 600,
      borderRadius: 1.5,
    }}
  />
);

const SolutionCard = ({ solution, active, onClick }) => {
  const Icon = solution.icon;
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onClick();
      }}
      sx={{
        cursor: 'pointer',
        border: `1px solid ${active ? alpha(solution.color, 0.48) : '#DEE2E6'}`,
        bgcolor: active ? alpha(solution.color, 0.1) : '#fff',
        borderRadius: 2,
        p: 2,
        transition: '180ms ease',
        minHeight: 118,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 14px 30px ${alpha(solution.color, 0.16)}`,
        },
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(solution.color, 0.14),
              color: solution.color,
            }}
          >
            <Icon />
          </Box>
          {active && <CheckRoundedIcon sx={{ color: solution.color }} />}
        </Stack>
        <Box>
          <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: 20 }}>{solution.title}</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 500 }}>{solution.subtitle}</Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const QuantitySlider = ({ label, value, onChange, disabled, color = '#2EB5C9', max = MAX_DEVICES_PER_SLIDER }) => (
  <Box sx={{ opacity: disabled ? 0.38 : 1 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
      <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: 15 }}>{label}</Typography>
      <Typography sx={{ color, fontWeight: 700 }}>{value}</Typography>
    </Stack>
    <Slider
      value={Number(value || 0)}
      min={0}
      max={max}
      step={1}
      disabled={disabled}
      onChange={(_, nextValue) => onChange(nextValue)}
      sx={{
        color,
        '& .MuiSlider-thumb': { width: 18, height: 18 },
        '& .MuiSlider-rail': { color: '#DEE2E6' },
      }}
    />
  </Box>
);

const LivePreview = ({ form, simulationCount, completed }) => {
  const selected = SOLUTIONS.filter((solution) => solutionEnabled(form, solution.key));
  const climateCount = climateDevicesCount(form);
  const energyCount = energyDevicesCount(form);
  const peopleCount = peopleDevicesCount(form);
  const metricsPreview = [
    { key: 'climate', label: 'Frío', value: climateCount, color: '#2EB5C9' },
    { key: 'energy', label: 'Energía', value: energyCount, color: '#F39711' },
    { key: 'people', label: 'Personas', value: peopleCount, color: '#5C149B' },
  ];
  const maxMetricValue = Math.max(...metricsPreview.map((item) => item.value), 1);

  return (
    <Box
      sx={{
        borderRadius: 3,
        p: { xs: 2, md: 2.5 },
        bgcolor: alpha('#ffffff', 0.93),
        border: '1px solid #DEE2E6',
        boxShadow: '0 14px 30px rgba(92, 20, 155, 0.12)',
        backdropFilter: 'blur(14px)',
      }}
    >
      <Stack spacing={2.25}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography sx={{ color: 'text.primary', fontWeight: 700, fontSize: 32 }}>
              {form.location_name || 'Tu demo Anubis'}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {form.company_name || 'Configura un escenario en vivo'}
            </Typography>
          </Box>
          <Box sx={{ width: 42, height: 42, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: completed ? '#3BD2A2' : '#5C149B', color: '#fff' }}>
            {completed ? <CheckRoundedIcon /> : simulationCount}
          </Box>
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha('#2EB5C9', 0.12) }}>
            <Typography sx={{ color: '#008598', fontSize: 12, fontWeight: 600 }}>Sensores</Typography>
            <Typography sx={{ color: 'text.primary', fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{simulationCount}</Typography>
          </Box>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha('#5C149B', 0.12) }}>
            <Typography sx={{ color: '#5C149B', fontSize: 12, fontWeight: 600 }}>Módulos</Typography>
            <Typography sx={{ color: 'text.primary', fontSize: 24, fontWeight: 700, lineHeight: 1 }}>{selected.length}</Typography>
          </Box>
        </Box>

        <Stack spacing={1}>
          {selected.length === 0 ? (
            <Typography sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Activa una solución y verás cómo se arma tu demo.
            </Typography>
          ) : selected.map((solution) => {
            const Icon = solution.icon;
            return (
              <Stack key={solution.key} direction="row" spacing={1.25} alignItems="center" sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha(solution.color, 0.09) }}>
                <Icon sx={{ color: solution.color }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>{solution.title}</Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 500 }}>{solution.subtitle}</Typography>
                </Box>
                <CheckRoundedIcon sx={{ color: solution.color }} />
              </Stack>
            );
          })}
        </Stack>

        <Box sx={{ borderRadius: 2, overflow: 'hidden', p: 1.5 }}>
          <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: 13, mb: 1.25 }}>
            Vista previa de métricas (distribución por módulo)
          </Typography>
          <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ minHeight: 82 }}>
            {metricsPreview.map((item) => (
              <Stack key={item.key} spacing={0.5} alignItems="center" sx={{ flex: 1 }}>
                <Typography sx={{ color: 'text.primary', fontSize: 11, fontWeight: 600 }}>
                  {item.value}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 44,
                    height: `${8 + ((item.value / maxMetricValue) * 54)}px`,
                    borderRadius: '8px 8px 0 0',
                    bgcolor: item.color,
                    transition: 'height 180ms ease',
                  }}
                />
                <Typography sx={{ color: 'text.secondary', fontSize: 11, fontWeight: 500 }}>
                  {item.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
};

const DemoRequestWizard = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const initializedRef = useRef(false);
  const debounceRef = useRef(null);
  const initialTokenRef = useRef(token);

  const progress = completed ? 100 : (step / (STEPS.length - 1)) * 100;
  const firstName = form.first_name?.trim() || 'Nombre';
  const activeStep = STEPS[step];

  const simulationCount = useMemo(() => calculateSimulationCount(form), [form]);

  useEffect(() => {
    let mounted = true;
    const initialToken = initialTokenRef.current;
    demoRequestLeadServices.createOrResume(initialToken ? { public_token: initialToken } : DEFAULT_FORM)
      .then((lead) => {
        if (!mounted) return;
        const nextToken = lead.public_token;
        setToken(nextToken);
        localStorage.setItem(TOKEN_KEY, nextToken);
        setForm((current) => ({ ...current, ...normalizeLead(lead) }));
        initializedRef.current = true;
      })
      .catch(() => setError('No pudimos iniciar el formulario. Inténtalo nuevamente en unos segundos.'))
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!initializedRef.current || !token || completed) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      demoRequestLeadServices.update(token, form)
        .catch(() => setError('No pudimos guardar los últimos cambios.'));
    }, 800);

    return () => clearTimeout(debounceRef.current);
  }, [form, token, completed]);

  const updateField = (field, value) => {
    setError('');
    setForm((current) => ({
      ...current,
      [field]: (() => {
        if (!numberFields.includes(field)) return value;
        const parsedValue = clampDeviceCount(value);
        if (!isDeviceFieldEnabled(current, field)) return 0;
        const currentFieldValue = Number(current[field] || 0);
        const baseTotal = calculateSimulationCount(current) - currentFieldValue;
        const maxForField = Math.max(0, MAX_TOTAL_DEVICES - baseTotal);
        return Math.min(parsedValue, maxForField);
      })(),
    }));
  };

  const toggleSolution = (key) => {
    setError('');
    setForm((current) => {
      if (key === 'climate') {
        const nextEnabled = !(current.measure_temperature || current.measure_humidity);
        const canAddDefaultDevice = calculateSimulationCount(current) < MAX_TOTAL_DEVICES;
        return {
          ...current,
          measure_temperature: nextEnabled,
          measure_humidity: nextEnabled,
          fridges_qty: nextEnabled && (current.fridges_qty + current.freezers_qty + current.ovens_qty === 0) && canAddDefaultDevice ? 1 : current.fridges_qty,
        };
      }
      if (key === 'energy') {
        const nextEnabled = !current.measure_energy;
        const canAddDefaultDevice = calculateSimulationCount(current) < MAX_TOTAL_DEVICES;
        return {
          ...current,
          measure_energy: nextEnabled,
          pm_energy_devices_qty: nextEnabled && (current.pm_energy_devices_qty + current.energy_devices_qty === 0) && canAddDefaultDevice ? 1 : current.pm_energy_devices_qty,
        };
      }
      const nextEnabled = !current.measure_people_count;
      const canAddDefaultDevice = calculateSimulationCount(current) < MAX_TOTAL_DEVICES;
      return {
        ...current,
        measure_people_count: nextEnabled,
        people_count_devices_qty: nextEnabled && current.people_count_devices_qty === 0 && canAddDefaultDevice ? 1 : current.people_count_devices_qty,
      };
    });
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.email.trim() || !form.first_name.trim() || !form.last_name.trim() || !form.company_name.trim()) {
        setError('Completa los datos de contacto obligatorios.');
        return false;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        setError('Ingresa un correo electrónico válido.');
        return false;
      }
    }
    if (step === 2 && !form.location_name.trim()) {
      setError('Indica el nombre del local o recinto que quieres simular.');
      return false;
    }
    if (step === 3) {
      if (!form.measure_temperature && !form.measure_humidity && !form.measure_energy && !form.measure_people_count) {
        setError('Selecciona al menos una solución para simular.');
        return false;
      }
      if (simulationCount === 0) {
        setError('Selecciona al menos un dispositivo para la simulación.');
        return false;
      }
      if (simulationCount > MAX_TOTAL_DEVICES) {
        setError(`Puedes seleccionar máximo ${MAX_TOTAL_DEVICES} dispositivos.`);
        return false;
      }
    }
    setError('');
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    setStep((current) => Math.min(current + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setError('');
    setStep((current) => Math.max(current - 1, 0));
  };

  const closeForm = () => {
    if (token) demoRequestLeadServices.update(token, form).finally(() => window.location.assign('/auth/login'));
    else window.location.assign('/auth/login');
  };

  const submit = () => {
    if (!validateStep() || !token) return;
    setSubmitting(true);
    setError('');
    demoRequestLeadServices.complete(token, form)
      .then((response) => {
        localStorage.removeItem(TOKEN_KEY);
        setCompleted(true);
        setMessage(response?.message || 'Recibimos tu solicitud. Te enviaremos las instrucciones por correo.');
      })
      .catch((err) => {
        const errors = err?.response?.data?.errors;
        setError(Array.isArray(errors) ? errors.join('. ') : 'No pudimos finalizar tu demo. Revisa los datos e intenta nuevamente.');
      })
      .finally(() => setSubmitting(false));
  };

  const renderStep = () => {
    if (completed) {
      return (
        <Stack spacing={2.5} alignItems="flex-start">
          <Box sx={{ width: 58, height: 58, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: alpha('#3BD2A2', 0.18), color: '#00A073' }}>
            <CheckRoundedIcon />
          </Box>
            <Typography sx={{ color: 'text.primary', fontSize: { xs: 30, md: 36 }, lineHeight: 1.06, fontWeight: 700 }}>
              Demo en marcha
            </Typography>
          <Typography sx={{ color: 'text.secondary', maxWidth: 540, fontSize: 17, fontWeight: 500 }}>
            {message}
          </Typography>
          <Button variant="contained" onClick={() => window.location.assign('/auth/login')} sx={{ bgcolor: '#5C149B', '&:hover': { bgcolor: '#5E3BB7' } }}>
            Ir al inicio
          </Button>
        </Stack>
      );
    }

    if (step === 0) {
      return (
        <Stack spacing={3}>
          <Stack spacing={1.5}>
            <Typography sx={{ color: 'text.primary', fontSize: { xs: 32, md: 40 }, lineHeight: 1.08, fontWeight: 700, maxWidth: 720 }}>
              Diseña tu primer centro IoT en menos de 2 minutos
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: { xs: 16, md: 18 }, maxWidth: 620, fontWeight: 500 }}>
              Elige sensores, mira cómo cambia la simulación y recibe acceso a un dashboard real con datos generados para tu caso.
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            {SOLUTIONS.map((solution) => (
              <DemoChip key={solution.key} active label={solution.title} />
            ))}
          </Stack>
          <Button variant="contained" size="large" onClick={goNext} endIcon={<ArrowForwardRoundedIcon />} sx={{ alignSelf: 'flex-start', bgcolor: '#5C149B', px: 4, py: 1.25, '&:hover': { bgcolor: '#5E3BB7' } }}>
            Crear mi demo
          </Button>
        </Stack>
      );
    }

    if (step === 1) {
      return (
        <Stack spacing={2.25}>
          <Typography sx={{ color: 'text.primary', fontWeight: 700, fontSize: { xs: 24, md: 30 }, lineHeight: 1.1 }}>
            Primero, pongámosle nombre al piloto
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Correo electrónico *" value={form.email} onChange={(event) => updateField('email', event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><EmailRoundedIcon /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nombre *" value={form.first_name} onChange={(event) => updateField('first_name', event.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Apellido *" value={form.last_name} onChange={(event) => updateField('last_name', event.target.value)} /></Grid>
            <Grid item xs={12} sm={7}><TextField fullWidth label="Empresa *" value={form.company_name} onChange={(event) => updateField('company_name', event.target.value)} /></Grid>
            <Grid item xs={12} sm={5}><TextField fullWidth label="Rubro" value={form.company_sector} onChange={(event) => updateField('company_sector', event.target.value)} /></Grid>
          </Grid>
        </Stack>
      );
    }

    if (step === 2) {
      return (
        <Stack spacing={2.5}>
          <Typography sx={{ color: 'text.primary', fontWeight: 700, fontSize: { xs: 24, md: 30 }, lineHeight: 1.1 }}>
            Bien {firstName}, ¿qué lugar quieres ver cobrar vida?
          </Typography>
          <TextField
            fullWidth
            label="Nombre del local o recinto *"
            placeholder="Sala de ventas, cocina central, bodega, sucursal norte..."
            value={form.location_name}
            onChange={(event) => updateField('location_name', event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><StorefrontRoundedIcon /></InputAdornment> }}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.25 }}>
            {['Sala de ventas', 'Cámara de frío', 'Acceso principal'].map((option) => (
              <Button key={option} variant="outlined" onClick={() => updateField('location_name', option)} sx={{ borderColor: '#DEE2E6', color: 'text.primary', justifyContent: 'flex-start' }}>
                {option}
              </Button>
            ))}
          </Box>
        </Stack>
      );
    }

    if (step === 3) {
      return (
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={{ color: 'text.primary', fontWeight: 700, fontSize: { xs: 24, md: 30 }, lineHeight: 1.1 }}>
              Arma el set de sensores para {form.location_name || 'tu recinto'}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontWeight: 500, mt: 1 }}>
              Cada selección actualiza la vista previa y define qué widgets tendrá tu dashboard.
            </Typography>
            <Typography sx={{ color: 'text.primary', fontSize: 13, fontWeight: 600, mt: 0.75 }}>
              Máximo {MAX_TOTAL_DEVICES} dispositivos en total ({simulationCount}/{MAX_TOTAL_DEVICES}).
            </Typography>
            {simulationCount === MAX_TOTAL_DEVICES && (
              <Alert
                severity="warning"
                sx={{
                  mt: 1.25,
                  border: `1px solid ${alpha('#F39711', 0.38)}`,
                  bgcolor: alpha('#F39711', 0.12),
                  '& .MuiAlert-message': { fontWeight: 600 },
                }}
              >
                Alcanzaste el máximo de {MAX_TOTAL_DEVICES} dispositivos. Para agregar otro, primero reduce alguno existente.
              </Alert>
            )}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
            {SOLUTIONS.map((solution) => (
              <SolutionCard key={solution.key} solution={solution} active={solutionEnabled(form, solution.key)} onClick={() => toggleSolution(solution.key)} />
            ))}
          </Box>

          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ border: '1px solid #DEE2E6', borderRadius: 2, p: 2, bgcolor: '#fff' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <ThermostatRoundedIcon sx={{ color: '#2EB5C9' }} />
                <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>Temperatura y humedad</Typography>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Button size="small" variant={form.measure_temperature ? 'contained' : 'outlined'} onClick={() => updateField('measure_temperature', !form.measure_temperature)} sx={{ bgcolor: form.measure_temperature ? '#2EB5C9' : undefined }}>
                  Temperatura
                </Button>
                <Button size="small" variant={form.measure_humidity ? 'contained' : 'outlined'} onClick={() => updateField('measure_humidity', !form.measure_humidity)} sx={{ bgcolor: form.measure_humidity ? '#2EB5C9' : undefined }}>
                  Temperatura y Humedad
                </Button>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}><QuantitySlider label="Refrigeradores" value={form.fridges_qty} disabled={!form.measure_temperature && !form.measure_humidity} onChange={(value) => updateField('fridges_qty', value)} color="#2EB5C9" /></Grid>
                <Grid item xs={12} md={4}><QuantitySlider label="Congeladores" value={form.freezers_qty} disabled={!form.measure_temperature && !form.measure_humidity} onChange={(value) => updateField('freezers_qty', value)} color="#2EB5C9" /></Grid>
                <Grid item xs={12} md={4}><QuantitySlider label="Hornos" value={form.ovens_qty} disabled={!form.measure_temperature && !form.measure_humidity} onChange={(value) => updateField('ovens_qty', value)} color="#2EB5C9" /></Grid>
              </Grid>
            </Box>

            <Box sx={{ border: '1px solid #DEE2E6', borderRadius: 2, p: 2, bgcolor: '#fff' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <BoltRoundedIcon sx={{ color: '#F39711' }} />
                <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>Energía y control remoto</Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}><QuantitySlider label="Con medición de consumo" value={form.pm_energy_devices_qty} disabled={!form.measure_energy} onChange={(value) => updateField('pm_energy_devices_qty', value)} color="#F39711" /></Grid>
                <Grid item xs={12} md={6}><QuantitySlider label="Solo control de encendido" value={form.energy_devices_qty} disabled={!form.measure_energy} onChange={(value) => updateField('energy_devices_qty', value)} color="#F39711" /></Grid>
              </Grid>
            </Box>

            <Box sx={{ border: '1px solid #DEE2E6', borderRadius: 2, p: 2, bgcolor: '#fff' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <GroupsRoundedIcon sx={{ color: '#5C149B' }} />
                <Typography sx={{ color: 'text.primary', fontWeight: 600 }}>Conteo de personas</Typography>
              </Stack>
              <QuantitySlider label="Contadores de acceso" value={form.people_count_devices_qty} disabled={!form.measure_people_count} onChange={(value) => updateField('people_count_devices_qty', value)} color="#5C149B" />
            </Box>
          </Box>
        </Stack>
      );
    }

    return (
      <Stack spacing={2.5}>
        <Typography sx={{ color: 'text.primary', fontSize: { xs: 24, md: 30 }, lineHeight: 1.1, fontWeight: 700 }}>
          Último dato para acompañarte
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontWeight: 500 }}>
          Si algo falla al generar la demo o quieres ayuda para revisarla, podremos contactarte rápido.
        </Typography>
        <TextField
          fullWidth
          label="Teléfono móvil"
          value={form.phone}
          onChange={(event) => updateField('phone', event.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><LocalPhoneRoundedIcon /></InputAdornment> }}
        />
      </Stack>
    );
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: '#F5F7FA' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        p: { xs: 1.25, md: 2 },
        bgcolor: '#F5F7FA',
        backgroundImage: 'linear-gradient(135deg, rgba(245,247,250,0.95), rgba(245,247,250,0.86)), url(/images/wall/agitalizr-unsplash.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Box sx={{ minHeight: 'calc(100vh - 32px)', display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px' }, gap: 2 }}>
        <Box sx={{ bgcolor: alpha('#fff', 0.94), border: '1px solid #DEE2E6', borderRadius: 4, p: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', boxShadow: '0 10px 28px rgba(92,20,155,0.08)' }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flex: 1, overflowX: 'auto' }}>
              {STEPS.map((label, index) => (
                <Chip
                  key={label}
                  label={label}
                  size="small"
                  color={index === step ? 'primary' : 'default'}
                  sx={{
                    fontWeight: 600,
                    bgcolor: index === step ? '#5C149B' : index < step || completed ? alpha('#3BD2A2', 0.18) : '#fff',
                    color: index === step ? '#fff' : index < step || completed ? '#00A073' : '#8595A6',
                  }}
                />
              ))}
            </Stack>
            <IconButton onClick={closeForm} sx={{ color: 'text.secondary', bgcolor: '#fff', '&:hover': { bgcolor: '#F5F7FA', color: 'text.primary' } }}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 99, bgcolor: '#DEE2E6', '& .MuiLinearProgress-bar': { bgcolor: '#5C149B' } }} />

          <Box sx={{ flex: 1, display: 'grid', alignItems: 'start', alignContent: 'start', maxWidth: 840, width: '100%', py: { xs: 4, md: 6 } }}>
            <Fade key={`${step}-${completed}`} in timeout={240}>
              <Box>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <DemoChip active label={activeStep} />
                  {simulationCount > 0 && <DemoChip active label={`${simulationCount} dispositivos`} />}
                </Stack>
                {!!error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {renderStep()}
              </Box>
            </Fade>
          </Box>

          {!completed && step > 0 && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Button onClick={goBack} startIcon={<ArrowBackRoundedIcon />} disabled={submitting} color="primary">
                Atrás
              </Button>
              {step < STEPS.length - 1 ? (
                <Button variant="contained" onClick={goNext} endIcon={<ArrowForwardRoundedIcon />} sx={{ bgcolor: '#5C149B', '&:hover': { bgcolor: '#5E3BB7' } }}>
                  Siguiente
                </Button>
              ) : (
                <Button variant="contained" onClick={submit} disabled={submitting} sx={{ bgcolor: '#5C149B', minWidth: 128, '&:hover': { bgcolor: '#5E3BB7' } }}>
                  {submitting ? <CircularProgress size={22} color="inherit" /> : 'Finalizar'}
                </Button>
              )}
            </Stack>
          )}
        </Box>

        <Box sx={{ display: { xs: step === 0 || completed ? 'block' : 'none', lg: 'block' }, alignSelf: 'stretch' }}>
          <Box sx={{ position: 'sticky', top: 16 }}>
            <LivePreview form={form} simulationCount={simulationCount} completed={completed} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DemoRequestWizard;
